import { useState, useCallback, useEffect } from 'react';
import type { Message, SessionConfig } from '../types';
import {
  loadSessionMetadata,
  saveSessionMetadata,
  loadSession,
  saveSession,
  deleteSession,
  createSessionId,
  setCurrentSessionId,
  getCurrentSessionId,
  deriveSessionTitle,
  deriveSessionPreview,
  type SessionData,
  type SessionMetadata,
} from '../utils/sessionHistory';
import { sanitizeMessagesForStorage } from '../utils/messageStorage';

interface UseSessionHistoryOptions {
  active: boolean;
  maxSessions: number;
  setMessages: (msgs: Message[]) => void;
  chatDispatch: (action: { type: 'SET_INPUT'; input: string }) => void;
  setThreadId: (id: string | null) => void;
  uiDispatch: (action: { type: 'CLEAR_ATTACHMENTS' } | { type: 'CLEAR_ATTACHMENT_ERRORS' }) => void;
  messages: Message[];
  threadId: string | null;
  sessionConfig?: SessionConfig;
}

export function useSessionHistory({
  active,
  maxSessions,
  setMessages,
  chatDispatch,
  setThreadId,
  uiDispatch,
  messages,
  threadId,
  sessionConfig,
}: UseSessionHistoryOptions) {
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessions, setSessions] = useState<SessionMetadata[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const maybeSessionStorage = sessionConfig?.storage === 'session' ? 'session' : undefined;

  const loadSessionMetadataSafe = useCallback(() => {
    return maybeSessionStorage ? loadSessionMetadata('session') : loadSessionMetadata();
  }, [maybeSessionStorage]);
  const saveSessionMetadataSafe = useCallback(
    (list: SessionMetadata[]) => {
      return maybeSessionStorage ? saveSessionMetadata(list, 'session') : saveSessionMetadata(list);
    },
    [maybeSessionStorage]
  );
  const loadSessionSafe = useCallback(
    (id: string) => (maybeSessionStorage ? loadSession(id, 'session') : loadSession(id)),
    [maybeSessionStorage]
  );
  const saveSessionSafe = useCallback(
    (data: SessionData) => (maybeSessionStorage ? saveSession(data, 'session') : saveSession(data)),
    [maybeSessionStorage]
  );
  const deleteSessionSafe = useCallback(
    (id: string) => (maybeSessionStorage ? deleteSession(id, 'session') : deleteSession(id)),
    [maybeSessionStorage]
  );
  const getCurrentSessionIdSafe = useCallback(() => {
    return maybeSessionStorage ? getCurrentSessionId('session') : getCurrentSessionId();
  }, [maybeSessionStorage]);
  const setCurrentSessionIdSafe = useCallback(
    (id: string | null) => {
      return maybeSessionStorage
        ? setCurrentSessionId(id, 'session')
        : setCurrentSessionId(id);
    },
    [maybeSessionStorage]
  );

  const applySessionLimit = useCallback(
    (list: SessionMetadata[]) => {
      if (!maxSessions || maxSessions <= 0) return { list, removed: [] as string[] };
      if (list.length <= maxSessions) return { list, removed: [] as string[] };
      const trimmed = list.slice(0, maxSessions);
      const removed = list.slice(maxSessions).map(meta => meta.id);
      return { list: trimmed, removed };
    },
    [maxSessions]
  );

  const createNewSession = useCallback(
    (existing?: SessionMetadata[]) => {
      const now = new Date().toISOString();
      const id = createSessionId();
      const title = 'New conversation';
      const sessionData: SessionData = {
        id,
        title,
        createdAt: now,
        lastModified: now,
        messages: [] as Message[],
        threadId: null,
      };
      const metadata: SessionMetadata = {
        id,
        title,
        createdAt: now,
        lastModified: now,
        messageCount: 0,
        preview: '',
      };

      saveSessionSafe(sessionData);
      const baseList = existing ?? loadSessionMetadataSafe();
      const merged = [metadata, ...baseList.filter(meta => meta.id !== id)].sort((a, b) =>
        b.lastModified.localeCompare(a.lastModified)
      );
      const { list, removed } = applySessionLimit(merged);
      saveSessionMetadataSafe(list);
      removed.forEach(deleteSessionSafe);
      setSessions(list);
      setActiveSessionId(id);
      setCurrentSessionIdSafe(id);
      setMessages([]);
      chatDispatch({ type: 'SET_INPUT', input: '' });
      setThreadId(null);
      uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      setSessionsOpen(false);
      return id;
    },

    [
      applySessionLimit,
      loadSessionMetadataSafe,
      saveSessionMetadataSafe,
      saveSessionSafe,
      setCurrentSessionIdSafe,
      deleteSessionSafe,
      setMessages,
      chatDispatch,
      setThreadId,
      uiDispatch,
    ]
  );

  const selectSession = useCallback(
    (id: string) => {
      const data = loadSessionSafe(id);
      if (!data) return;
      setActiveSessionId(id);
      setCurrentSessionIdSafe(id);
      setMessages(data.messages || []);
      chatDispatch({ type: 'SET_INPUT', input: '' });
      setThreadId(data.threadId ?? null);
      uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      setSessionsOpen(false);
    },

    [loadSessionSafe, setCurrentSessionIdSafe, setMessages, chatDispatch, setThreadId, uiDispatch]
  );

  const removeSession = useCallback(
    (id: string) => {
      deleteSessionSafe(id);
      const updated = loadSessionMetadataSafe().filter(meta => meta.id !== id);
      saveSessionMetadataSafe(updated);
      setSessions(updated);
      if (id === activeSessionId) {
        createNewSession(updated);
      }
    },
    [activeSessionId, createNewSession, deleteSessionSafe, loadSessionMetadataSafe, saveSessionMetadataSafe]
  );

  // Initialize: load session list and restore active session
  useEffect(() => {
    if (!active) return;
    const metadata = loadSessionMetadataSafe();
    setSessions(metadata);
    const storedId = getCurrentSessionIdSafe();
    const valid = storedId && metadata.some(meta => meta.id === storedId) ? storedId : null;
    if (!valid) {
      createNewSession(metadata);
      return;
    }
    setActiveSessionId(valid);
    const data = loadSessionSafe(valid);
    if (data?.messages) setMessages(data.messages);
    if (data?.threadId !== undefined) setThreadId(data.threadId);
  }, [
    active,
    createNewSession,
    getCurrentSessionIdSafe,
    loadSessionMetadataSafe,
    loadSessionSafe,
    setMessages,
    setThreadId,
  ]);

  // Auto-save: persist current session whenever messages or threadId change
  useEffect(() => {
    if (!active || !activeSessionId) return;
    const now = new Date().toISOString();
    const existing = loadSessionSafe(activeSessionId);
    const createdAt = existing?.createdAt || now;
    const title = deriveSessionTitle(messages);
    const preview = deriveSessionPreview(messages);
    const storedMessages = sanitizeMessagesForStorage(messages);
    const sessionData: SessionData = {
      id: activeSessionId,
      title,
      createdAt,
      lastModified: now,
      messages: storedMessages,
      threadId: threadId ?? null,
    };
    saveSessionSafe(sessionData);

    const metadata: SessionMetadata = {
      id: activeSessionId,
      title,
      createdAt,
      lastModified: now,
      messageCount: messages.length,
      preview,
    };
    const merged = [
      metadata,
      ...loadSessionMetadataSafe().filter(meta => meta.id !== activeSessionId),
    ].sort((a, b) => b.lastModified.localeCompare(a.lastModified));
    const { list, removed } = applySessionLimit(merged);
    saveSessionMetadataSafe(list);
    removed.forEach(deleteSessionSafe);
    setSessions(list);
    setCurrentSessionIdSafe(activeSessionId);
  }, [
    active,
    activeSessionId,
    applySessionLimit,
    deleteSessionSafe,
    messages,
    loadSessionMetadataSafe,
    loadSessionSafe,
    saveSessionMetadataSafe,
    saveSessionSafe,
    setCurrentSessionIdSafe,
    threadId,
  ]);

  return {
    sessionsOpen,
    setSessionsOpen,
    sessions,
    activeSessionId,
    createNewSession,
    selectSession,
    removeSession,
  };
}
